import React from 'react';
import classNames from 'classnames';
import Store from 'store';
import Data from 'data';
import Locale from 'data/locale';

import NumberInput from 'ui/number';
import { paragonGetInfo, paragonGetTabPoints, paragonSetPoints, paragonSetLevel, paragonTable, paragonAddPoints, paragonCanReset, paragonResetTab, paragonResetAll } from 'logic/paragon';
import { formatNumber } from 'utils/format';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

import './Paragon.scss';
import TooltipBuilder from 'tooltips/builder';
import Tooltip from 'ui/tooltip';
import AdUnit from 'AdUnit';
import { Seasons } from 'common';
import { AltarSection, EmbedLink } from './Altar';

const _L = Locale("ui-paragon.js");

const useClass = () => Store.useProfile(profile => profile.class);

function ParagonLevel() {
  const seasonal = Store.useProfile(profile => profile.seasonal);
  const level = Store.useProfile(profile => profile.paragon.level);
  const dispatch = Store.useDispatch();
  const max = seasonal === Seasons.Enmity ? 800 : undefined;

  const onChange = React.useCallback(value => dispatch.profile.setParagon((paragon, profile) => paragonSetLevel(paragonTable(profile.seasonal), paragon, value)), [dispatch]);

  return <NumberInput min={0} max={max} value={level} onChange={onChange}/>;
}

function ParagonAddTip() {
  const tip = new TooltipBuilder(_L`Click to add points`);
  tip.line(_L`Shift-click to add 10 points`);
  tip.line(_L`Ctrl-click to add 100 points`);
  tip.line(_L`Alt-click to add all available points`);
  return tip.build();
}

const ParagonStat = React.memo(function ParagonStat({ tab, index, data, unspent }) {
  const charClass = useClass();
  const dispatch = Store.useDispatch();
  const value = Store.useProfile(profile => profile.paragon.data[tab][index]);

  const onChange = React.useCallback(value => dispatch.profile.setParagon((paragon, profile) => paragonSetPoints(paragonTable(profile.seasonal), paragon, tab, index, value)), [dispatch, tab, index]);

  const onAddClick = React.useCallback(e => {
    let amount = 1;
    if (e.altKey) amount = Infinity;
    else if (e.ctrlKey) amount = 100;
    else if (e.shiftKey) amount = 10;
    dispatch.profile.setParagon((paragon, profile) => paragonAddPoints(paragonTable(profile.seasonal), paragon, tab, index, amount));
  }, [dispatch, tab, index]);

  const [stat, amount, icon] = paragonGetInfo(data, charClass);
  const canIncrease = (data.limit == null || value < data.limit) && unspent;
  return (
    <li>
      <span className="d3p-ParagonIcon" style={{backgroundPosition: `${-icon * 24}px 0`}}/>
      <span>{Data.stats[stat].name}</span>
      <NumberInput className="d3p-ParagonValue" min={0} max={data.limit} value={value} onChange={onChange}/>
      <Tooltip tooltip={<ParagonAddTip/>}>
        <span className={classNames("d3p-ParagonAdd", {"d3p-paragon-spent": !canIncrease})} onClick={onAddClick}>
          <FontAwesomeIcon icon={faPlus}/>
        </span>
      </Tooltip>
      <span className="d3p-ParagonEffect">
        +{formatNumber(value * amount, data.decimal, 10000)}{data.percent ? "%" : ""}
      </span>
    </li>
  );
});

function ParagonTab({ tab, data }) {
  const dispatch = Store.useDispatch();
  const canReset = Store.useProfile(profile => paragonCanReset(paragonTable(profile.seasonal), profile.paragon, tab), [tab]);
  const remaining = Store.useProfile(profile => {
    const points = paragonGetTabPoints(tab, profile.paragon.level);
    const spent = profile.paragon.data[tab].reduce((a, b) => a + b, 0);
    return points - spent;
  }, [tab]);
  const onReset = React.useCallback(() => dispatch.profile.setParagon((paragon, profile) => paragonResetTab(paragonTable(profile.seasonal), paragon, tab)), [dispatch, tab]);
  return (
    <div className="d3p-ParagonTab">
      <h3>
        {data.name}
        {canReset && <span className="d3p-ParagonTabReset" onClick={onReset}>{_L`(reset)`}</span>}
        {remaining > 0 && <span className="d3p-ParagonUnspent">{_L`${remaining} unspent points`}</span>}
      </h3>
      <ul>
        {data.stats.map((stat, idx) => <ParagonStat key={idx} tab={tab} index={idx} data={stat} unspent={remaining > 0}/>)}
      </ul>
    </div>
  );
}

export function ParagonEmbed({ style }) {
  const [tab, setTab] = React.useState(0);
  const charClass = useClass();
  const paragon = Store.useProfile(profile => profile.paragon);
  const table = Store.useProfile(profile => paragonTable(profile.seasonal));

  return (
    <div className="d3p-ParagonEmbed" style={style}>
      <div className="d3p-tabs">
        {table.map((data, index) => (
          <div className={classNames("d3p-tab", {"d3p-active": index === tab})} key={index} onClick={() => setTab(index)}>{data.name}</div>
        ))}
      </div>
      <div className="d3p-body">
        {table[tab].stats.map((data, index) => {
          const [stat, amount, icon] = paragonGetInfo(data, charClass);
          const value = paragon.data[tab][index];
          return (
            <div className={classNames("d3p-entry", {"d3p-empty": !value, "d3p-full": data.limit && value >= data.limit})} key={index}>
              <div className="d3p-icon" style={{backgroundPosition: `${-icon}em 0`}}/>
              <div className="d3p-name">{Data.stats[stat].name}</div>
              <div className="d3p-effect">+{formatNumber((value || 1) * amount, data.decimal, 10000)}{data.percent ? "%" : ""}</div>
              <div className="d3p-points">{value}{data.limit ? `/${data.limit}` : ''}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default React.memo(function Paragon() {
  const seasonal = Store.useProfile(profile => profile.seasonal);
  const dispatch = Store.useDispatch();
  const table = paragonTable(seasonal);
  const onReset = React.useCallback(() => dispatch.profile.setParagon((paragon, profile) => paragonResetAll(paragonTable(profile.seasonal), paragon)), [dispatch]);
  return (
    <div className="d3p-TabContent d3p-Paragon">
      <div className="d3p-ParagonHeader">
        <span className="d3p-ParagonReset" onClick={onReset}>{_L`Reset all`}</span>
        <EmbedLink type="paragon"/>
        <span className="d3p-ParagonLevel">
          {_L`Paragon level`}
          <ParagonLevel/>
        </span>
      </div>
      <ParagonTab tab={0} data={table[0]}/>
      <ParagonTab tab={1} data={table[1]}/>
      <AdUnit id="d3planner-tab-paragon" className="d3planner-tab-ad d3planner-tab-ad-middle"/>
      <ParagonTab tab={2} data={table[2]}/>
      <ParagonTab tab={3} data={table[3]}/>
      {seasonal !== Seasons.None && <AltarSection/>}
    </div>
  );
});
